/**
 * Application Setting `rpi.allowedUserEmails` — JSON array of emails allowed
 * for Inspected By / Disposition By on Returned Product Inspection.
 *
 * Missing / empty setting → no allowlist (show all active users).
 */

export const RPI_ALLOWED_USER_EMAILS_SETTING_KEY = 'rpi.allowedUserEmails';

/**
 * @param {string|{ value?: string }|null|undefined} settingValue
 * @returns {string[]} lowercase emails (empty = no restriction)
 */
export function parseRpiAllowedUserEmails(settingValue) {
  if (settingValue == null) return [];

  let raw = settingValue;
  if (typeof settingValue === 'object' && settingValue !== null && 'value' in settingValue) {
    raw = settingValue.value;
  }

  if (typeof raw !== 'string') return [];
  const trimmed = raw.trim();
  if (!trimmed || trimmed === '[]') return [];

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((e) => typeof e === 'string' && e.includes('@'))
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean);
    }
  } catch {
    /* comma-separated fallback */
  }

  return trimmed
    .split(/[\s,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((e) => e.includes('@'));
}

/**
 * @param {string[]} allowedEmails lowercase
 * @returns {(user: object) => boolean}
 */
export function createRpiAllowedUserFilter(allowedEmails) {
  const allowed = new Set((allowedEmails || []).map((e) => String(e).toLowerCase()));
  return (user) => {
    if (!user || user.userIsActive === false) return false;
    const email = (user.userEmail || '').trim().toLowerCase();
    return email && allowed.has(email);
  };
}

/**
 * Apply allowlist lookupFilter to inspectedById / dispositionById fields.
 * Empty allowlist leaves fields unfiltered (all users).
 * @param {object} config
 * @param {string[]} allowedEmails
 */
export function withRpiAllowedUserFilters(config, allowedEmails) {
  if (!allowedEmails?.length) {
    return config;
  }

  const lookupFilter = createRpiAllowedUserFilter(allowedEmails);
  const fields = (config.fields || []).map((field) => {
    if (field.name !== 'inspectedById' && field.name !== 'dispositionById') {
      return field;
    }
    return { ...field, lookupFilter };
  });
  return { ...config, fields };
}
